import React, { useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Page, { Grid, GridColumn } from '@atlaskit/page';
import { Box, xcss, Inline } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';

import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import { PersonalSettingsBreadcrumbItem } from '@atlassian/jira-personal-settings-common/src/view/index.tsx';
import { DIGEST_NOTIFICATIONS_PERSONAL_SETTINGS_PATH } from '@atlassian/jira-personal-settings-paths/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { ui_DigestsNotificationsPageQuery } from '@atlassian/jira-relay/src/__generated__/ui_DigestsNotificationsPageQuery.graphql.ts';
import { FeedbackCollectorButton } from '@atlassian/jira-feedback-collector/src/ui/button/index.tsx';
import { ENTRYPOINT_ID_JIRA_PLATFORM } from '@atlassian/jira-feedback-collector/src/constants.tsx';
import type { combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_inProductPreferenceFragment$key } from '@atlassian/jira-relay/src/__generated__/combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_inProductPreferenceFragment.graphql.ts';
import type { combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_emailPreferenceFragment$key } from '@atlassian/jira-relay/src/__generated__/combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_emailPreferenceFragment.graphql.ts';
import type { emailPrefDueDateNotif_personalSettingsDigestNotifications_EmailPrefDueDateNotification$key } from '@atlassian/jira-relay/src/__generated__/emailPrefDueDateNotif_personalSettingsDigestNotifications_EmailPrefDueDateNotification.graphql.ts';
import type { inProductPrefDueDateNotif_personalSettingsDigestNotifications_InProductPrefDueDateNotification$key } from '@atlassian/jira-relay/src/__generated__/inProductPrefDueDateNotif_personalSettingsDigestNotifications_InProductPrefDueDateNotification.graphql.ts';

import type { combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_emailFragment$key } from '@atlassian/jira-relay/src/__generated__/combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_emailFragment.graphql';
import type { combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_inProductFragment$key } from '@atlassian/jira-relay/src/__generated__/combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_inProductFragment.graphql';
import type { combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_slackFragment$key } from '@atlassian/jira-relay/src/__generated__/combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_slackFragment.graphql';
import type { emailInviterNotif_personalSettingsDigestNotifications_EmailInviterNotification$key } from '@atlassian/jira-relay/src/__generated__/emailInviterNotif_personalSettingsDigestNotifications_EmailInviterNotification.graphql';
import type { inProductInviterNotif_personalSettingsDigestNotifications_InProductInviterNotification$key } from '@atlassian/jira-relay/src/__generated__/inProductInviterNotif_personalSettingsDigestNotifications_InProductInviterNotification.graphql';
import type { slackInviterNotif_personalSettingsDigestNotifications_SlackInviterNotification$key } from '@atlassian/jira-relay/src/__generated__/slackInviterNotif_personalSettingsDigestNotifications_SlackInviterNotification.graphql';

import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';

import type { combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_slackPreferenceFragment$key } from '@atlassian/jira-relay/src/__generated__/combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_slackPreferenceFragment.graphql.ts';
import type { slackPrefDueDateNotif_personalSettingsDigestNotifications_SlackPrefDueDateNotification$key } from '@atlassian/jira-relay/src/__generated__/slackPrefDueDateNotif_personalSettingsDigestNotifications_SlackPrefDueDateNotification.graphql.ts';
import { InProductPrefDueDateNotification } from '../common/ui/in-product-pref-due-date-notif/index.tsx';
import { CombinedPrefDueDateNotification } from '../common/ui/combined-pref-due-date-notif/index.tsx';
import { EmailPrefDueDateNotification } from '../common/ui/email-pref-due-date-notif/index.tsx';

import { CombinedInviterNotification } from '../common/ui/combined-inviter-notif/index.tsx';
import { EmailInviterNotification } from '../common/ui/email-inviter-notif/index.tsx';
import { InProductInviterNotification } from '../common/ui/in-product-inviter-notif/index.tsx';
import { SlackInviterNotification } from '../common/ui/slack-inviter-notif/index.tsx';

import { SlackPrefDueDateNotification } from '../common/ui/slack-pref-due-date-notif/index.tsx';
import messages from './messages.tsx';

export const DigestsNotificationsPage = () => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();

	const [expConfig] = UNSAFE_noExposureExp('jira_inviter_on_join_notification');
	const [slackExpConfig] = UNSAFE_noExposureExp('due-date-notifications-slack-integration');

	const data = useLazyLoadQuery<ui_DigestsNotificationsPageQuery>(
		graphql`
			query ui_DigestsNotificationsPageQuery($cloudId: ID!) {
				jira @required(action: THROW) {
					notificationGlobalPreference(cloudId: $cloudId)
						@required(action: THROW)
						@optIn(to: "JiraNotificationPreference") {
						preferences {
							...combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_emailPreferenceFragment
							...combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_inProductPreferenceFragment
							...combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_slackPreferenceFragment
							...emailPrefDueDateNotif_personalSettingsDigestNotifications_EmailPrefDueDateNotification
							...inProductPrefDueDateNotif_personalSettingsDigestNotifications_InProductPrefDueDateNotification
							...slackPrefDueDateNotif_personalSettingsDigestNotifications_SlackPrefDueDateNotification
							dailyDueDateNotification @optIn(to: "JiraDueDateNotificationsToggle") {
								emailChannel {
									isEnabled
								}
								inProductChannel {
									isEnabled
								}
								slackChannel {
									isEnabled
								}
							}

							...combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_emailFragment
							...combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_inProductFragment
							...combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_slackFragment
							...emailInviterNotif_personalSettingsDigestNotifications_EmailInviterNotification
							...inProductInviterNotif_personalSettingsDigestNotifications_InProductInviterNotification
							...slackInviterNotif_personalSettingsDigestNotifications_SlackInviterNotification
							projectInviterNotification {
								emailChannel {
									isEnabled
								}
								inProductChannel {
									isEnabled
								}
								slackChannel {
									isEnabled
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
		},
	);

	const emailChannelEnabled =
		data.jira.notificationGlobalPreference.preferences?.dailyDueDateNotification?.emailChannel
			?.isEnabled ?? false;
	const inProductChannelEnabled =
		data.jira.notificationGlobalPreference.preferences?.dailyDueDateNotification?.inProductChannel
			?.isEnabled ?? false;
	const slackChannelEnabled =
		data.jira.notificationGlobalPreference.preferences?.dailyDueDateNotification?.slackChannel
			?.isEnabled ?? false;

	const [isAnyChannelEnabled, setIsAnyChannelEnabled] = useState<boolean>(
		emailChannelEnabled ||
			inProductChannelEnabled ||
			(slackExpConfig.get('isEnabled', 'False') === 'True' && slackChannelEnabled),
	);

	const inviterEmailEnabled =
		data.jira.notificationGlobalPreference.preferences?.projectInviterNotification?.emailChannel
			?.isEnabled ?? false;
	const inviterInProductEnabled =
		data.jira.notificationGlobalPreference.preferences?.projectInviterNotification?.inProductChannel
			?.isEnabled ?? false;
	const inviterSlackEnabled =
		data.jira.notificationGlobalPreference.preferences?.projectInviterNotification?.slackChannel
			?.isEnabled ?? false;

	const [isAnyInviterNotifEnabled, setIsAnyInviterNotifEnabled] = useState<boolean>(
		inviterEmailEnabled || inviterInProductEnabled || inviterSlackEnabled,
	);

	/* eslint-disable @typescript-eslint/consistent-type-assertions */
	const preferences = data.jira.notificationGlobalPreference
		.preferences as unknown as combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_emailPreferenceFragment$key &
		combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_inProductPreferenceFragment$key &
		combinedPrefDueDateNotif_personalSettingsDigestNotifications_CombinedPrefDueDateNotification_slackPreferenceFragment$key &
		emailPrefDueDateNotif_personalSettingsDigestNotifications_EmailPrefDueDateNotification$key &
		inProductPrefDueDateNotif_personalSettingsDigestNotifications_InProductPrefDueDateNotification$key &
		slackPrefDueDateNotif_personalSettingsDigestNotifications_SlackPrefDueDateNotification$key &
		combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_emailFragment$key &
		combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_inProductFragment$key &
		combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_slackFragment$key &
		emailInviterNotif_personalSettingsDigestNotifications_EmailInviterNotification$key &
		inProductInviterNotif_personalSettingsDigestNotifications_InProductInviterNotification$key &
		slackInviterNotif_personalSettingsDigestNotifications_SlackInviterNotification$key;
	/* eslint-enable @typescript-eslint/consistent-type-assertions */

	return (
		<Page testId="personal-settings-digest-notifications.ui.main">
			<Grid layout="fixed" spacing="comfortable">
				<GridColumn>
					<PageHeader
						breadcrumbs={
							<Breadcrumbs>
								<PersonalSettingsBreadcrumbItem />
								<BreadcrumbsItem
									href={DIGEST_NOTIFICATIONS_PERSONAL_SETTINGS_PATH}
									text={formatMessage(messages.digestsNotificationsPageTitle)}
								/>
							</Breadcrumbs>
						}
						bottomBar={
							<>
								<Inline space="space.050">
									{formatMessage(messages.digestsNotificationsExplanation)}
								</Inline>
							</>
						}
						actions={
							<FeedbackCollectorButton
								entrypointId={ENTRYPOINT_ID_JIRA_PLATFORM}
								feedbackGroupId="personal-settings-digests"
							/>
						}
					>
						{formatMessage(messages.digestsNotificationsPageTitle)}
					</PageHeader>

					<Box>{formatMessage(messages.digestsNotificationsControl)}</Box>

					<br />

					<CombinedPrefDueDateNotification
						onToggleChange={setIsAnyChannelEnabled}
						emailPreferenceFragment={preferences}
						inProductPreferenceFragment={preferences}
						slackPreferenceFragment={preferences}
					/>
					{isAnyChannelEnabled && (
						<Box
							testId="personal-settings-digest-notifications.ui.box"
							xcss={[contentWrapperStyles]}
							role="group"
							aria-labelledby="due-date-notif-prefs-label"
						>
							<Box id="due-date-notif-prefs-label" xcss={preferenceRowStyles}>
								{formatMessage(messages.digestsNotificationsReceiveNotifications)}
							</Box>
							<Box xcss={preferenceRowStyles}>
								<EmailPrefDueDateNotification preferenceFragment={preferences} />
							</Box>
							{slackExpConfig.get('isEnabled', 'False') === 'True' ? (
								<>
									<Box xcss={preferenceRowStyles}>
										<InProductPrefDueDateNotification preferenceFragment={preferences} />
									</Box>
									<Box xcss={[preferenceRowStyles, preferenceRowLastStyles]}>
										<SlackPrefDueDateNotification preferenceFragment={preferences} />
									</Box>
								</>
							) : (
								<Box xcss={[preferenceRowStyles, preferenceRowLastStyles]}>
									<InProductPrefDueDateNotification preferenceFragment={preferences} />
								</Box>
							)}
						</Box>
					)}

					{expConfig.get('isEnabled', false) && (
						<>
							<br />
							<CombinedInviterNotification
								onToggleChange={setIsAnyInviterNotifEnabled}
								emailFragment={preferences}
								inProductFragment={preferences}
								slackFragment={preferences}
							/>
							{isAnyInviterNotifEnabled && (
								<Box
									testId="personal-settings-digest-notifications.ui.box"
									xcss={[contentWrapperStyles]}
								>
									<Box xcss={preferenceRowStyles}>
										{formatMessage(messages.digestsNotificationsReceiveNotifications)}
									</Box>
									<Box xcss={preferenceRowStyles}>
										<EmailInviterNotification preferenceFragment={preferences} />
									</Box>
									<Box xcss={[preferenceRowStyles]}>
										<InProductInviterNotification preferenceFragment={preferences} />
									</Box>
									<Box xcss={[preferenceRowStyles, preferenceRowLastStyles]}>
										<SlackInviterNotification preferenceFragment={preferences} />
									</Box>
								</Box>
							)}
						</>
					)}
				</GridColumn>
			</Grid>
		</Page>
	);
};

const contentWrapperStyles = xcss({
	backgroundColor: 'elevation.surface',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'border.radius.100',
	borderTop: 'none',
	borderTopLeftRadius: '0px',
	borderTopRightRadius: '0px',
	paddingRight: 'space.200',
	paddingLeft: 'space.200',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
});

const preferenceRowStyles = xcss({
	marginBottom: 'space.100',
});

const preferenceRowLastStyles = xcss({ marginBottom: 'space.0' });
